package diana.soleil.design.iraniancanadienbazzar.feature.dashboard

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.common.Formatters
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.RatingStars
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ReviewCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.Review
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_error
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_owner_reply_label
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_reply_action
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_reply_failed
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_reply_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_reply_send
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_retry
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_summary_desc
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_summary_title
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.resources.mr_title
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** Stateful entry for Manage Reviews (one owned business). */
@Composable
fun ManageReviewsRoute(
    businessId: String,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: ManageReviewsViewModel = koinViewModel(parameters = { parametersOf(businessId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    ManageReviewsScreen(
        state = state,
        onBack = onBack,
        onReply = viewModel::reply,
        modifier = modifier,
    )
}

/**
 * Manage Reviews (Stitch `953ead76`): teal summary card (live average + count), then each review with
 * an inline reply composer (or the existing reply). Owners may write ONLY `ownerReply` — rules enforce
 * it. Tokens + string resources only.
 */
@Composable
fun ManageReviewsScreen(
    state: ManageReviewsUiState,
    onBack: () -> Unit,
    onReply: (reviewId: String, text: String) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (state.error) {
        ErrorState(
            message = stringResource(Res.string.mr_error),
            onRetry = onBack,
            retryLabel = stringResource(Res.string.mr_retry),
            modifier = modifier,
        )
        return
    }
    Box(modifier = modifier.background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.contentMaxWidth).fillMaxHeight().statusBarsPadding().navigationBarsPadding()) {
        LazyColumn(
            contentPadding = PaddingValues(
                start = AppTheme.spacing.lg,
                end = AppTheme.spacing.lg,
                top = AppTheme.spacing.md,
                bottom = AppTheme.spacing.xl,
            ),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            item {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                    AppIconButton(
                        iconName = "arrow_back",
                        contentDescription = stringResource(Res.string.mr_back_a11y),
                        onClick = onBack,
                        tint = AppTheme.colors.onSurface,
                    )
                    Text(
                        text = stringResource(Res.string.mr_title),
                        style = AppTheme.typography.headlineLg,
                        color = AppTheme.colors.onSurface,
                    )
                }
            }

            if (state.loading) {
                item { LoadingState() }
                return@LazyColumn
            }

            item {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppTheme.colors.primaryContainer, AppTheme.shapes.card)
                        .padding(AppTheme.spacing.lg),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                ) {
                    Text(
                        text = stringResource(Res.string.mr_summary_title),
                        style = AppTheme.typography.headlineMd,
                        color = AppTheme.colors.onPrimary,
                    )
                    Text(
                        text = stringResource(Res.string.mr_summary_desc, state.reviews.size),
                        style = AppTheme.typography.bodyMd,
                        color = AppTheme.colors.onPrimary,
                    )
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                        Text(
                            text = if (state.reviews.isEmpty()) "—" else Formatters.rating(state.averageRating.toFloat()),
                            style = AppTheme.typography.displayLg,
                            color = AppTheme.colors.onPrimary,
                        )
                        RatingStars(rating = state.averageRating.toFloat())
                    }
                }
            }

            if (state.reviews.isEmpty()) {
                item {
                    EmptyState(
                        title = stringResource(Res.string.mr_empty_title),
                        description = stringResource(Res.string.mr_empty_desc),
                    )
                }
            } else {
                items(count = state.reviews.size, key = { state.reviews[it].id }) { index ->
                    val review = state.reviews[index]
                    ReviewWithReply(
                        review = review,
                        busy = review.id in state.busyIds,
                        failed = review.id in state.failedIds,
                        onReply = { text -> onReply(review.id, text) },
                    )
                }
            }
        }
    }
}

@Composable
private fun ReviewWithReply(
    review: Review,
    busy: Boolean,
    failed: Boolean,
    onReply: (String) -> Unit,
) {
    var composing by remember(review.id) { mutableStateOf(false) }
    var draft by remember(review.id) { mutableStateOf(review.ownerReply.orEmpty()) }

    AppCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            ReviewCard(
                authorName = review.authorName,
                rating = review.rating.toFloat(),
                comment = review.comment,
            )
            val reply = review.ownerReply
            if (!reply.isNullOrBlank() && !composing) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppTheme.colors.surfaceContainer, AppTheme.shapes.md)
                        .padding(AppTheme.spacing.md),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                ) {
                    Text(
                        text = stringResource(Res.string.mr_owner_reply_label),
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.primary,
                    )
                    Text(text = reply, style = AppTheme.typography.bodyMd, color = AppTheme.colors.onSurface)
                }
            }
            if (failed) {
                Text(
                    text = stringResource(Res.string.mr_reply_failed),
                    style = AppTheme.typography.labelSm,
                    color = AppTheme.colors.error,
                )
            }
            if (composing) {
                AppTextField(
                    value = draft,
                    onValueChange = { draft = it },
                    placeholder = stringResource(Res.string.mr_reply_placeholder),
                    modifier = Modifier.fillMaxWidth(),
                )
                AppButton(
                    text = stringResource(Res.string.mr_reply_send),
                    onClick = {
                        onReply(draft)
                        composing = false
                    },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !busy && draft.isNotBlank(),
                    loading = busy,
                )
            } else {
                AppButton(
                    text = stringResource(Res.string.mr_reply_action),
                    onClick = { composing = true },
                    modifier = Modifier.fillMaxWidth(),
                    variant = AppButtonVariant.Secondary,
                    enabled = !busy,
                    leadingIcon = "reply",
                )
            }
        }
    }
}
